import React, { useEffect, useMemo, useState } from 'react';
import { Flag } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { formatDate } from '../../utils/helpers';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import { t } from '../../i18n';

const MILESTONE_STATUS_TONE = {
  Completado: 'success',
  'En proceso': 'warning',
  Pendiente: 'neutral'
};

const ProjectMilestones = ({ embedded = false, parkId = '' }) => {
  const { renewablePortfolio, renewableMilestones, preferences, updatePreferences } = useAppContext();
  const preferredParkId = parkId || preferences?.projectContext?.parkId || 'all';
  const defaultProjectId = useMemo(() => {
    if (!renewablePortfolio || renewablePortfolio.length === 0) return '';
    if (preferredParkId !== 'all') {
      const match = renewablePortfolio.find((project) => project.parkId === preferredParkId);
      if (match) return match.id;
    }
    return renewablePortfolio[0]?.id || '';
  }, [renewablePortfolio, preferredParkId]);
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId);
  const [statusFilter, setStatusFilter] = useState('Todos');

  const selectedProject = useMemo(
    () => renewablePortfolio.find((project) => project.id === selectedProjectId) || null,
    [renewablePortfolio, selectedProjectId]
  );

  useEffect(() => {
    if (!defaultProjectId) return;
    setSelectedProjectId(defaultProjectId);
  }, [defaultProjectId]);

  useEffect(() => {
    const parkId = selectedProject?.parkId || null;
    if (!parkId) return;
    updatePreferences({ projectContext: { parkId } });
  }, [selectedProject?.parkId, updatePreferences]);

  const milestones = useMemo(
    () => renewableMilestones.filter((item) => item.projectId === selectedProjectId),
    [renewableMilestones, selectedProjectId]
  );

  const filtered = useMemo(() => {
    if (statusFilter === 'Todos') return milestones;
    return milestones.filter((item) => item.status === statusFilter);
  }, [milestones, statusFilter]);

  const tableData = useMemo(
    () => filtered.map((milestone) => ({ ...milestone, dateLabel: formatDate(milestone.date) })),
    [filtered]
  );

  const columns = useMemo(
    () => [
      {
        key: 'title',
        label: t('Hito'),
        sortable: true,
        render: (value) => (
          <span className="inline-flex items-center gap-2">
            <Flag className="w-3.5 h-3.5 text-neutral-400" />
            <span className="font-medium text-neutral-800">{t(value)}</span>
          </span>
        )
      },
      { key: 'dateLabel', label: t('Fecha'), sortable: true },
      {
        key: 'status',
        label: t('Estado'),
        sortable: true,
        render: (value) => <Badge tone={MILESTONE_STATUS_TONE[value] || 'neutral'}>{t(value)}</Badge>
      }
    ],
    []
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('Registro de Hitos')}</h1>
            <p className="text-sm text-slate-500">{t('Hitos criticos y fechas clave del proyecto.')}</p>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              {renewablePortfolio.map((project) => (
                <option key={project.id} value={project.id}>
                  {t(project.name)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              <option>{t('Todos')}</option>
              <option>{t('Completado')}</option>
              <option>{t('En proceso')}</option>
              <option>{t('Pendiente')}</option>
            </select>
          </div>
        </div>
      )}

      <DataTable
        data={tableData}
        columns={columns}
        searchPlaceholder={t('Buscar hitos...')}
        emptyTitle={t('Sin hitos para este filtro')}
        emptyDescription={t('Ajusta el proyecto o el estado seleccionado.')}
      />
    </div>
  );
};

export default ProjectMilestones;
